/**
 * Promo code rules (pure, shared by browser and server).
 *
 * The server resolves a code from the database (or the two built-in codes),
 * checks it with `evaluatePromo`, and computes the discount with
 * `promoDiscount` — the browser uses the same functions only for display.
 */
export type PromoCode = {
  code: string;
  label: string;
  type: "percent" | "amount";
  value: number;
};

/** The two original CB16 codes — values unchanged, always available unless an admin disables them. */
export const PROMO_CODES: Record<string, PromoCode> = {
  CB16WELCOME: { code: "CB16WELCOME", label: "Welcome 10%", type: "percent", value: 10 },
  MAISON20: { code: "MAISON20", label: "Maison ₹20", type: "amount", value: 20 },
};

/** A code that passed validation and is applied to the bag. */
export type AppliedPromo = PromoCode & {
  /** Cap on the discount amount (₹); null = no cap. */
  maxDiscount?: number | null;
  /** Products the discount applies to; null = every item in the bag. */
  eligibleProductIds?: string[] | null;
  /** Minimum bag subtotal (₹) required; null = none. */
  minOrderValue?: number | null;
};

/** Stored promo definition (database row or built-in). */
export type PromoRecord = {
  code: string;
  label: string;
  type: "percent" | "amount";
  value: number;
  isActive: boolean;
  isSystem: boolean;
  startsAt: string | null;
  endsAt: string | null;
  minOrderValue: number | null;
  maxDiscount: number | null;
  usageLimit: number | null;
  /** Already resolved from eligible products + collections; null = all products. */
  eligibleProductIds: string[] | null;
};

export type PromoLine = { productId: string; unitPrice: number; quantity: number };

export type PromoFailure =
  | "not_found"
  | "inactive"
  | "not_started"
  | "expired"
  | "usage_limit"
  | "min_order"
  | "not_eligible";

export type PromoEvaluation =
  | { ok: true; promo: AppliedPromo; discount: number }
  | { ok: false; reason: PromoFailure; message: string };

export function normalizePromoCode(code: string | null | undefined): string {
  return (code ?? "").trim().toUpperCase();
}

/** Default terms of a built-in code before any admin edit: active, no dates, limits or product rules. */
export function builtInPromo(code: string): PromoRecord | null {
  const p = PROMO_CODES[normalizePromoCode(code)];
  if (!p) return null;
  return {
    code: p.code,
    label: p.label,
    type: p.type,
    value: p.value,
    isActive: true,
    isSystem: true,
    startsAt: null,
    endsAt: null,
    minOrderValue: null,
    maxDiscount: null,
    usageLimit: null,
    eligibleProductIds: null,
  };
}

/** A saved promo row, already parsed (eligible products resolved separately). */
export type StoredPromo = Omit<PromoRecord, "eligibleProductIds"> & { deleted: boolean };

/**
 * Resolves the terms that apply to a code:
 * - a saved row wins (built-in or custom), including every admin edit;
 * - a deleted row means the code can no longer be used, even a built-in one;
 * - with no saved row, built-in codes keep their original defaults.
 */
export function resolvePromoTerms(
  code: string,
  stored: StoredPromo | null | undefined,
): Omit<PromoRecord, "eligibleProductIds"> | null {
  if (stored) {
    if (stored.deleted) return null;
    const { deleted: _deleted, ...terms } = stored;
    return { ...terms, isSystem: terms.isSystem || !!PROMO_CODES[normalizePromoCode(code)] };
  }
  const base = builtInPromo(code);
  if (!base) return null;
  const { eligibleProductIds: _e, ...terms } = base;
  return terms;
}

/** Whether a code with these terms can be offered right now (status, dates, usage). */
export function promoCurrentlyAvailable(
  terms: Pick<PromoRecord, "isActive" | "startsAt" | "endsAt" | "usageLimit">,
  opts: { now?: Date; usedCount?: number } = {},
): boolean {
  const now = (opts.now ?? new Date()).getTime();
  if (!terms.isActive) return false;
  if (terms.startsAt && new Date(terms.startsAt).getTime() > now) return false;
  if (terms.endsAt && new Date(terms.endsAt).getTime() < now) return false;
  if (terms.usageLimit && (opts.usedCount ?? 0) >= terms.usageLimit) return false;
  return true;
}

const rupees = (n: number) => `₹${Math.round(n).toLocaleString("en-IN")}`;

/** Discount (₹, whole rupees) a valid promo gives on these bag lines. */
export function promoDiscount(lines: PromoLine[], promo: AppliedPromo | null | undefined): number {
  if (!promo) return 0;
  const subtotal = lines.reduce((s, l) => s + l.unitPrice * l.quantity, 0);
  const eligible = promo.eligibleProductIds?.length
    ? lines
        .filter((l) => promo.eligibleProductIds!.includes(l.productId))
        .reduce((s, l) => s + l.unitPrice * l.quantity, 0)
    : subtotal;
  if (eligible <= 0) return 0;
  if (promo.minOrderValue && subtotal < promo.minOrderValue) return 0;
  let discount = promo.type === "percent" ? Math.round((eligible * promo.value) / 100) : Math.min(promo.value, eligible);
  if (promo.maxDiscount && promo.maxDiscount > 0) discount = Math.min(discount, promo.maxDiscount);
  return Math.max(0, Math.min(Math.round(discount), Math.round(subtotal)));
}

/** Checks a stored promo against the bag, date and usage so far. */
export function evaluatePromo(
  record: PromoRecord | null,
  lines: PromoLine[],
  opts: { now?: Date; usedCount?: number } = {},
): PromoEvaluation {
  if (!record) return { ok: false, reason: "not_found", message: "This promo code isn't recognised." };
  const now = opts.now ?? new Date();
  if (!record.isActive) return { ok: false, reason: "inactive", message: `${record.code} is not active at the moment.` };
  if (record.startsAt && now < new Date(record.startsAt)) {
    return { ok: false, reason: "not_started", message: `${record.code} isn't available yet.` };
  }
  if (record.endsAt && now > new Date(record.endsAt)) {
    return { ok: false, reason: "expired", message: `${record.code} has expired.` };
  }
  if (record.usageLimit && (opts.usedCount ?? 0) >= record.usageLimit) {
    return { ok: false, reason: "usage_limit", message: `${record.code} has reached its usage limit.` };
  }
  const subtotal = lines.reduce((s, l) => s + l.unitPrice * l.quantity, 0);
  if (record.minOrderValue && subtotal < record.minOrderValue) {
    return {
      ok: false,
      reason: "min_order",
      message: `${record.code} needs a minimum order of ${rupees(record.minOrderValue)}.`,
    };
  }
  const promo: AppliedPromo = {
    code: record.code,
    label: record.label || record.code,
    type: record.type,
    value: record.value,
    maxDiscount: record.maxDiscount,
    eligibleProductIds: record.eligibleProductIds,
    minOrderValue: record.minOrderValue,
  };
  const discount = promoDiscount(lines, promo);
  if (discount <= 0) {
    return { ok: false, reason: "not_eligible", message: `${record.code} doesn't apply to the items in your bag.` };
  }
  return { ok: true, promo, discount };
}

/** An offer customers can see and tap at checkout. */
export type PublicOffer = {
  code: string;
  label: string;
  type: "percent" | "amount";
  value: number;
  minOrderValue: number | null;
  maxDiscount: number | null;
  endsAt: string | null;
  restricted: boolean;
};
